import { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import styles from './ExerciseStepEditor.module.css';
import { RemarkCodeBlockPlugin } from '../CodeBlockRenderer/remarkCodeBlockPlugin';
import { TextInput } from '../../common/TextInput';
import { RemarkYoutubePlugin } from '../YoutubePlugin';
import { getDefaultColorDefinitions } from '../CodeBlockRenderer/platformColorDefaults';
import { Popover } from '../../common/Popover';

export const ExerciseStepEditor = ({ name, content, onContentChange, onNameChange, platform }: ExerciseStepEditorProps) => {
  const [showColorPopover, setShowColorPopover] = useState(false);
  const iconRef = useRef<HTMLButtonElement>(null);

  const colorDefinitions = getDefaultColorDefinitions(platform);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = content.substring(0, start) + '  ' + content.substring(end);
      onContentChange(newContent);
      // Set cursor position after the inserted spaces
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };

  return (
    <div>
      <TextInput label="Name" value={name} onChange={val => onNameChange(val)} type="text" />

      <div className={styles.editor}>
        <div className={styles.leftPanel}>
          <div className={styles.editorHeader}>
            <h2>
              Opdracht Editor
              <button
                ref={iconRef}
                className={styles.infoIcon}
                onClick={() => setShowColorPopover(!showColorPopover)}
                type="button"
                aria-label="Toon beschikbare kleuren"
              >
                i
              </button>
            </h2>
            {colorDefinitions.size > 0 && (
              <Popover isOpen={showColorPopover} onClose={() => setShowColorPopover(false)} title={`Beschikbare kleuren voor ${platform}`}>
                <div className={styles.colorList}>
                  {Array.from(colorDefinitions.entries()).map(([name, color]) => (
                    <div key={name} className={styles.colorItem}>
                      <div className={styles.colorCircle} style={{ backgroundColor: color }} />
                      <span className={styles.colorName}>{name}</span>
                    </div>
                  ))}
                </div>
              </Popover>
            )}
          </div>
          <textarea
            className={styles.markdownEditor}
            value={content}
            onChange={e => onContentChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Voer hier je markdown in..."
          />
        </div>
        <div className={styles.divider} />
        <div className={styles.rightPanel}>
          <div className={styles.previewHeader}>
            <h2>Voorbeeld</h2>
          </div>
          <div className={styles.preview}>
            <div className={styles.markdownContent}>
              <ReactMarkdown
                remarkPlugins={[[RemarkCodeBlockPlugin.plugin, { platform }], RemarkYoutubePlugin.plugin]}
                components={{ ...RemarkCodeBlockPlugin.components, ...RemarkYoutubePlugin.components }}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export type ExerciseStepEditorProps = {
  name: string;
  content: string;
  platform?: string;

  onNameChange: (name: string) => void;
  onContentChange: (content: string) => void;
};
